import { format, parse, parseISO, isValid, addMinutes } from 'date-fns';
import type { ExperienceTimeSlotItemResource } from '@anyroad/api';
import type { TimeSlot } from '@anyroad/time-slot-selector';
import type { BookingDate, PricingRule } from './types';

/**
 * Parse booking date from either a structured date object or a date string.
 * Structured date object is preferred as it avoids locale-specific parsing issues.
 * @param dateInput - Either a structured date object {year, month, day} or a date string
 */
export function parseBookingDate(
  dateInput: BookingDate | string | null | undefined
): Date | null {
  if (!dateInput) return null;

  // If structured date object (preferred), construct date from numeric values
  if (typeof dateInput === 'object' && 'year' in dateInput) {
    const { year, month, day } = dateInput;

    // Validate all fields are present and positive
    if (!year || !month || !day || year < 0 || month < 1 || day < 1) {
      return null;
    }

    // Validate month is in valid range (1-12 from backend)
    if (month > 12) {
      return null;
    }

    // Month is 1-indexed from backend, Date constructor expects 0-indexed
    const date = new Date(year, month - 1, day);

    // Validate the date is valid and matches the input values
    // (e.g., Feb 30 would roll over to March, we want to reject that)
    if (!isValid(date)) {
      return null;
    }

    // Verify the constructed date matches the input values
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  // Fallback: parse string (locale-dependent, may fail for non-English)
  if (typeof dateInput === 'string') {
    const s = dateInput.trim();
    if (!s) return null;
    const iso = parseISO(s);
    if (isValid(iso)) return iso;
    try {
      const d = parse(s, 'MMM d, yyyy', new Date());
      return isValid(d) ? d : null;
    } catch {
      return null;
    }
  }

  return null;
}

/** Normalize time string to 24h "HH:mm" for comparison with API slot.time. */
export function normalizeTimeTo24h(timeStr: string): string {
  if (!timeStr?.trim()) return '';
  const s = timeStr.trim();
  // Check if it's already in 24h format (with or without zero-padding)
  if (/^([01]?\d|2[0-3]):[0-5]\d$/u.test(s)) {
    // Parse and format to ensure zero-padding (e.g. "9:30" -> "09:30")
    const d = parse(s, 'H:mm', new Date());
    if (isValid(d)) return format(d, 'HH:mm');
    return s;
  }
  try {
    const d = parse(s, 'h:mm a', new Date());
    if (isValid(d)) return format(d, 'HH:mm');
    const d2 = parse(s, 'HH:mm', new Date());
    return isValid(d2) ? format(d2, 'HH:mm') : s;
  } catch {
    return s;
  }
}

/**
 * Get currency symbol from currency code.
 */
export function getCurrencySymbol(currencyCode: string): string {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    JPY: '¥',
    CNY: '¥',
    CAD: 'CA$',
    AUD: 'A$',
    CHF: 'CHF',
    INR: '₹',
    KRW: '₩',
    MXN: 'MX$',
    BRL: 'R$',
  };
  return symbols[currencyCode] || currencyCode;
}

/**
 * Parse amount from API: supports formatted strings with various currency symbols.
 * Returns a number for comparison; 0 if unparseable.
 */
export function parseAmountDue(value: string): number {
  if (value == null || value === '') return 0;
  // Strip common currency symbols and formatting characters
  const stripped = value.replace(/[$€£¥₹₩,\s]/g, '').trim();
  const n = parseFloat(stripped);
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Display string for payment details with proper currency formatting.
 * For refund we show the absolute value.
 */
export function formatAmountDueForDisplay(
  value: string,
  isRefund: boolean,
  currencyCode: string = 'USD'
): string {
  const n = parseAmountDue(value);
  const symbol = getCurrencySymbol(currencyCode);

  if (isRefund && n < 0) return `${symbol}${Math.abs(n).toFixed(2)}`;
  if (
    value != null &&
    value !== '' &&
    value.replace(/[$€£¥₹₩,\s]/g, '').trim() !== ''
  )
    return value;
  return `${symbol}${Math.abs(n).toFixed(2)}`;
}

export function formatCurrencyAmount(
  currencyCode: string,
  amount: number
): string {
  return `${getCurrencySymbol(currencyCode)}${amount.toFixed(2)}`;
}

/**
 * Check if a time slot is within the reschedule cutoff window.
 * Returns true if the slot is too close to the start time to be rescheduled.
 * @param slotDate - Date string in YYYY-MM-DD format
 * @param slotTime - Time string in HH:mm format
 * @param minutesBeforeCutoff - Minutes before the slot when rescheduling is no longer allowed
 */
export function isSlotPastRescheduleCutoff(
  slotDate: string,
  slotTime: string,
  minutesBeforeCutoff: number
): boolean {
  if (!slotDate || !slotTime || minutesBeforeCutoff == null) return false;

  try {
    const slotDateTime = parseISO(`${slotDate}T${slotTime}`);
    if (!isValid(slotDateTime)) return false;

    const cutoffTime = addMinutes(slotDateTime, -minutesBeforeCutoff);
    const now = new Date();

    return now >= cutoffTime;
  } catch {
    return false;
  }
}

/**
 * Map API time slot to TimeSlot format expected by TimeSlotSelector component.
 * Preserves pricing_rule.ticket_types (with available/capacity per type) when present for capacity-by-ticket-type checks.
 */
export function mapExperienceTimeSlotToTimeSlot(
  slot: ExperienceTimeSlotItemResource
): TimeSlot {
  const pricingRule = slot?.pricing_rule;
  return {
    available_spots: slot.available,
    created: slot.created,
    date: slot.date,
    id: slot.id,
    pricing_rule: pricingRule
      ? {
          days_of_week: pricingRule.days_of_week || [],
          end_date: pricingRule.end_date,
          end_time: pricingRule.end_time,
          experience_id: pricingRule.experience_id,
          message: pricingRule.message || null,
          name: pricingRule.name,
          start_date: pricingRule.start_date,
          start_time: pricingRule.start_time,
          type: pricingRule.type as 'advanced' | 'base',
          ...(pricingRule.ticket_types?.length
            ? {
                ticket_types: pricingRule.ticket_types.map((ticketType) => ({
                  id: ticketType.id,
                  available:
                    'available' in ticketType
                      ? (ticketType as { available: number }).available
                      : 0,
                  capacity:
                    'capacity' in ticketType
                      ? (ticketType as { capacity: number }).capacity
                      : 0,
                  name: ticketType.name,
                  plural_name: ticketType.plural_name,
                  price: ticketType.price,
                })),
              }
            : {}),
        }
      : null,
    time: slot.time,
    updated: slot.updated,
    url: slot.url,
  };
}

/**
 * Build a map of unique pricing rule messages from time slots.
 * If an effectiveSlotId is provided, only show that slot's pricing rule (or all if it has none).
 * Otherwise, show all unique pricing rules.
 */
export function buildPricingRulesMap(
  timeSlotsItems: ExperienceTimeSlotItemResource[] | undefined,
  effectiveSlotId: string | null
): Map<string, PricingRule> | null {
  if (!timeSlotsItems?.length) {
    return null;
  }

  const messagesSet = new Set<string>();

  if (effectiveSlotId) {
    const selectedSlot = timeSlotsItems.find(
      (slot) => String(slot.id) === String(effectiveSlotId)
    );
    if (selectedSlot?.pricing_rule?.message) {
      messagesSet.add(selectedSlot.pricing_rule.message);
    } else {
      // If selected slot has no pricing rule message, show all
      timeSlotsItems.forEach((slot) => {
        if (slot.pricing_rule?.message) {
          messagesSet.add(slot.pricing_rule.message);
        }
      });
    }
  } else {
    // No effective slot, show all pricing rules
    timeSlotsItems.forEach((slot) => {
      if (slot.pricing_rule?.message) {
        messagesSet.add(slot.pricing_rule.message);
      }
    });
  }

  if (messagesSet.size === 0) {
    return null;
  }

  // Convert Set to Map with message as the key and generate IDs from index
  const rulesMap = new Map<string, PricingRule>(
    Array.from(messagesSet).map((message, index) => [
      message,
      {
        id: index + 1,
        message,
        ticket_types: [],
      },
    ])
  );

  return rulesMap;
}

/**
 * Finds the ID of the time slot matching the current booking time.
 *
 * Only returns an ID if we're viewing the current booking date. This is used
 * to determine if the user is selecting the same time slot they already have,
 * which would disable the save button.
 *
 * @param params - Parameters for finding the current slot
 * @param params.timeSlots - Array of available time slots from the API
 * @param params.currentBookingDateStr - The date of the current booking (yyyy-MM-dd format)
 * @param params.selectedDateStr - The currently selected date in the UI (yyyy-MM-dd format)
 * @param params.currentBookingTime24 - The time of the current booking in 24-hour format (HH:mm)
 * @returns The ID of the matching time slot, or null if not found or not viewing current booking date
 * ```
 */
export const findCurrentBookingSlotId = ({
  timeSlots,
  currentBookingDateStr,
  selectedDateStr,
  currentBookingTime24,
}: {
  timeSlots: ExperienceTimeSlotItemResource[] | undefined;
  currentBookingDateStr: string | null;
  selectedDateStr: string | undefined;
  currentBookingTime24: string;
}): string | null => {
  // Only look for current slot if viewing the current booking date
  const isViewingCurrentBookingDate =
    currentBookingDateStr && selectedDateStr === currentBookingDateStr;

  if (!isViewingCurrentBookingDate || !timeSlots?.length) {
    return null;
  }

  // Find slot matching current booking time
  // Check both the raw time and normalized time to handle different formats
  const matchingSlot = timeSlots.find(
    (slot) =>
      slot.time === currentBookingTime24 ||
      normalizeTimeTo24h(slot.time) === currentBookingTime24
  );

  return matchingSlot?.id ?? null;
};

export const isSaveButtonDisabled = ({
  selectedDate,
  selectedTimeId,
  bookingIdHash,
  currentSlotId,
  isDiffPending,
  isReschedulePending,
}: {
  selectedDate: Date | null;
  selectedTimeId: string | undefined;
  bookingIdHash: string | undefined;
  currentSlotId: string | null;
  isDiffPending: boolean;
  isReschedulePending: boolean;
}): boolean => {
  return (
    !selectedDate ||
    !selectedTimeId ||
    !bookingIdHash ||
    selectedTimeId === currentSlotId ||
    isDiffPending ||
    isReschedulePending
  );
};
